import React, { useState, useEffect, useRef, useCallback } from "react";
import { MsalProvider, useMsal, useIsAuthenticated } from "@azure/msal-react";
import { PublicClientApplication } from "@azure/msal-browser";
import { msalConfig, loginRequest, ADMIN_EMAILS } from "./authConfig";

const msalInstance = new PublicClientApplication(msalConfig);

// ─── Constants ────────────────────────────────────────────────────────────────
const PROJECT_OPTIONS = ["VA", "USAF", "ARMY", "ADMIN", "OVERHEAD"];
const MONTH_NAMES = ["January","February","March","April","May","June",
                     "July","August","September","October","November","December"];

const GLOBAL_STYLES = `
  @import url('https://fonts.googleapis.com/css2?family=DM+Sans:wght@400;500;600;700&family=DM+Mono:wght@400;500&display=swap');

  *, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }

  body { font-family: 'DM Sans', sans-serif; background: #EDF6F9; }

  /* ── Mobile responsive ── */
  @media (max-width: 600px) {
    .ts-grid-header { display: none !important; }
    .ts-grid-row {
      display: flex !important;
      flex-direction: column !important;
      padding: 8px 10px !important;
      gap: 6px !important;
    }
    .ts-grid-day { display: flex; align-items: center; gap: 8px; }
    .ts-grid-actions { display: flex; gap: 6px; justify-content: flex-end; }
    .ts-grid-actions button { flex: 1; padding: 8px 0 !important; font-size: 13px !important; }
    .ts-select { font-size: 14px !important; padding: 8px 10px !important; }
    .ts-input { font-size: 14px !important; padding: 8px 10px !important; }
    .header-top { flex-wrap: wrap; gap: 8px; }
    .header-sub { flex-wrap: wrap; gap: 8px; padding: 8px 14px !important; }
    .user-section { border-left: none !important; padding-left: 0 !important; flex-wrap: wrap; }
    .month-total { display: none; }
  }

  /* ── Buttons — Flat 2D ── */
  .btn {
    display: inline-flex; align-items: center; justify-content: center; gap: 7px;
    font-family: 'DM Sans', sans-serif; font-weight: 600; cursor: pointer;
    border-radius: 8px; border: none; transition: all 0.15s ease;
    white-space: nowrap; text-decoration: none; outline: none;
  }
  .btn:active { opacity: 0.85; }

  .btn-primary {
    background: #2C497F; color: #fff; padding: 9px 20px; font-size: 13px;
    border: 1px solid #1a2d50;
  }
  .btn-primary:hover { background: #3355a0; }

  .btn-submit {
    background: #2C497F; color: #fff; padding: 9px 20px; font-size: 13px;
    border: 1px solid #1a2d50;
  }
  .btn-submit:hover { background: #3355a0; }
  .btn-submit:disabled { opacity: 0.45; cursor: default; }

  .btn-approve {
    background: #E8F3F2; color: #2C497F;
    border: 1.5px solid #A3CCD0;
    padding: 4px 12px; font-size: 11px; border-radius: 6px;
  }
  .btn-approve:hover { background: #d4ecf0; border-color: #2C497F; }
  .btn-approve.active { background: #2C497F; color: #fff; border-color: #2C497F; }

  .btn-reject {
    background: #EDF6F9; color: #2C497F;
    border: 1.5px solid #A3CCD0;
    padding: 4px 12px; font-size: 11px; border-radius: 6px;
  }
  .btn-reject:hover { background: #E3E4FA; border-color: #2C497F; }
  .btn-reject.active { background: #2C497F; color: #fff; border-color: #2C497F; }

  .btn-ghost {
    background: #fff; color: #2C497F;
    border: 1.5px solid #A3CCD0;
    padding: 3px 8px; font-size: 12px; border-radius: 6px; font-weight: 600;
  }
  .btn-ghost:hover { background: #E8F3F2; border-color: #2C497F; }

  .btn-danger {
    background: #fff; color: #2C497F;
    border: 1.5px solid #A3CCD0;
    padding: 3px 8px; font-size: 11px; border-radius: 6px; font-weight: 600;
  }
  .btn-danger:hover { background: #E3E4FA; border-color: #2C497F; }

  .btn-recall {
    background: #fff; color: #2C497F;
    border: 1.5px solid #A3CCD0;
    padding: 5px 14px; font-size: 11px; border-radius: 6px; font-weight: 600;
  }
  .btn-recall:hover { background: #E8F3F2; border-color: #2C497F; }

  /* ── Inputs & Selects ── */
  .ts-select {
    font-family: 'DM Sans', sans-serif; font-size: 12px; outline: none;
    width: 100%; border-radius: 6px; padding: 5px 8px; cursor: pointer;
    transition: border-color 0.15s, box-shadow 0.15s; appearance: none;
    background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='10' height='6' viewBox='0 0 10 6'%3E%3Cpath d='M1 1l4 4 4-4' stroke='%23A6A6A6' stroke-width='1.5' fill='none' stroke-linecap='round'/%3E%3C/svg%3E");
    background-repeat: no-repeat; background-position: right 8px center;
    padding-right: 26px;
  }
  .ts-select:focus { box-shadow: 0 0 0 2px rgba(44,73,127,0.2); }
  .ts-select:disabled { cursor: default; opacity: 0.75; }

  .ts-input {
    font-family: 'DM Mono', monospace; font-size: 12px; outline: none;
    width: 100%; border-radius: 6px; padding: 5px 8px; text-align: center;
    transition: border-color 0.15s, box-shadow 0.15s;
  }
  .ts-input:focus { box-shadow: 0 0 0 2px rgba(44,73,127,0.2); }
  .ts-input:disabled { cursor: default; opacity: 0.75; }

  /* ── Day row hover ── */
  .day-row { transition: background 0.1s; }
  .day-row:hover { background: #E8F3F2 !important; }
  .day-row.weekend:hover { background: #E3E4FA !important; }
  .day-row.today:hover { background: #E8F3F2 !important; }

  /* ── Spinner ── */
  @keyframes spin { to { transform: rotate(360deg); } }
  .spinner {
    width: 28px; height: 28px;
    border: 3px solid #E3E4FA; border-top-color: #2C497F;
    border-radius: 50%; animation: spin 0.7s linear infinite;
  }

  /* ── Card hover ── */
  .sub-card { transition: box-shadow 0.15s; }
  .sub-card:hover { box-shadow: 0 4px 16px rgba(44,73,127,0.1) !important; }

  /* ── Fade in ── */
  @keyframes fadeIn { from { opacity:0; transform:translateY(6px); } to { opacity:1; transform:translateY(0); } }
  .fade-in { animation: fadeIn 0.25s ease both; }

  /* ── Modal backdrop ── */
  .modal-backdrop {
    position: fixed; inset: 0; background: rgba(44,73,127,0.45);
    backdrop-filter: blur(3px); display: flex; align-items: center;
    justify-content: center; z-index: 1000; animation: fadeIn 0.15s ease;
  }
  .modal-card {
    background: #fff; border-radius: 14px; padding: 28px;
    max-width: 420px; width: 90%; box-shadow: 0 24px 64px rgba(44,73,127,0.18);
    animation: fadeIn 0.2s ease;
  }

  /* ── Saved badge ── */
  @keyframes savedPop { 0%{opacity:0;transform:scale(0.8)} 20%{opacity:1;transform:scale(1.05)} 100%{opacity:1;transform:scale(1)} }
  .saved-badge { animation: savedPop 0.3s ease both; }
`;

// ─── Colors ───────────────────────────────────────────────────────────────────
const C = {
  // Brand palette
  navy:         "#2C497F",
  purple:       "#2C497F",
  lavender:     "#E3E4FA",
  teal:         "#A3CCD0",
  tealLight:    "#E8F3F2",
  skyLight:     "#EDF6F9",
  gray:         "#A6A6A6",

  // Semantic mappings — all from brand palette
  bg:           "#EDF6F9",
  surface:      "#ffffff",
  surfaceAlt:   "#E8F3F2",
  border:       "#A3CCD0",
  borderLight:  "#E3E4FA",
  header:       "#2C497F",
  accent:       "#2C497F",
  text:         "#2C497F",
  textMid:      "#4a6090",
  textMuted:    "#A6A6A6",
  weekend:      "#E3E4FA",
  weekendBorder:"#A3CCD0",
  today:        "#E8F3F2",
  todayBorder:  "#2C497F",
  success:      "#2C497F",
  successBg:    "#E8F3F2",
  approved:     "#2C497F",
  approvedBg:   "#E8F3F2",
  approvedBorder:"#A3CCD0",
  rejected:     "#2C497F",
  rejectedBg:   "#E3E4FA",
  rejectedBorder:"#2C497F",
  submitted:    "#2C497F",
  submittedBg:  "#E3E4FA",
  submittedBorder:"#2C497F",
  draft:        "#A6A6A6",
  draftBg:      "#EDF6F9",
  draftBorder:  "#A3CCD0",
};

const STATUS = { DRAFT: "draft", SUBMITTED: "submitted", APPROVED: "approved", REJECTED: "rejected" };

const PROJECT_COLORS = {
  VA:       { bg:"#E8F3F2", color:"#2C497F", border:"#A3CCD0" },
  USAF:     { bg:"#EDF6F9", color:"#2C497F", border:"#A3CCD0" },
  ARMY:     { bg:"#E3E4FA", color:"#2C497F", border:"#A3CCD0" },
  ADMIN:    { bg:"#E3E4FA", color:"#2C497F", border:"#2C497F" },
  OVERHEAD: { bg:"#EDF6F9", color:"#A6A6A6", border:"#A3CCD0" },
};

// ─── Status Pill ──────────────────────────────────────────────────────────────
function StatusPill({ status, small = false }) {
  const cfg = {
    [STATUS.DRAFT]:     { bg:C.draftBg,    color:C.draft,    border:C.draftBorder,    label:"Draft",     icon:"✏" },
    [STATUS.SUBMITTED]: { bg:C.submittedBg, color:C.submitted,border:C.submittedBorder,label:"Submitted", icon:"📤" },
    [STATUS.APPROVED]:  { bg:C.approvedBg,  color:C.approved, border:C.approvedBorder, label:"Approved",  icon:"✓" },
    [STATUS.REJECTED]:  { bg:C.rejectedBg,  color:C.rejected, border:C.rejectedBorder, label:"Rejected",  icon:"✕" },
  }[status] || { bg:C.draftBg, color:C.draft, border:C.draftBorder, label:"Draft", icon:"✏" };
  return (
    <span style={{
      display:"inline-flex", alignItems:"center", gap:"4px",
      background:cfg.bg, color:cfg.color, border:`1.5px solid ${cfg.border}`,
      padding: small ? "1px 7px" : "3px 10px",
      borderRadius:"20px", fontSize: small ? "9px" : "11px",
      fontWeight:"700", whiteSpace:"nowrap", letterSpacing:"0.02em",
    }}>
      <span>{cfg.icon}</span> {cfg.label}
    </span>
  );
}

// ─── Project Badge ────────────────────────────────────────────────────────────
function ProjectBadge({ project }) {
  if (!project) return null;
  const c = PROJECT_COLORS[project] || { bg:"#E8F3F2", color:"#2C497F", border:"#A3CCD0" };
  return (
    <span style={{
      display:"inline-flex", alignItems:"center",
      background:c.bg, color:c.color, border:`1.5px solid ${c.border}`,
      padding:"1px 8px", borderRadius:"5px", fontSize:"10px", fontWeight:"700", letterSpacing:"0.05em",
    }}>{project}</span>
  );
}

// ─── Date Helpers ─────────────────────────────────────────────────────────────
const getDaysInMonth = (y, m) => new Date(y, m + 1, 0).getDate();
const pad = (n) => String(n).padStart(2, "0");
const dateKey = (y, m, d) => `${y}-${pad(m + 1)}-${pad(d)}`;
const getDayName = (y, m, d) => new Date(y, m, d).toLocaleDateString("en-US", { weekday: "short" });
const isWeekend = (y, m, d) => { const w = new Date(y, m, d).getDay(); return w === 0 || w === 6; };
const uid = () => Math.random().toString(36).slice(2, 9);
const fmtDt = (iso) => iso ? new Date(iso).toLocaleDateString("en-US", { month:"short", day:"numeric", hour:"2-digit", minute:"2-digit" }) : "";
const monthKey = (y, m) => `${y}-${pad(m + 1)}`;

// ─── API Helper ───────────────────────────────────────────────────────────────
async function api(path, options = {}) {
  const res = await fetch(path, {
    headers: { "Content-Type": "application/json", ...options.headers },
    ...options,
    body: options.body ? JSON.stringify(options.body) : undefined,
  });
  if (!res.ok) throw new Error(`API ${path} failed: ${res.status}`);
  return res.json();
}

// ─── Login Screen ─────────────────────────────────────────────────────────────
function LoginScreen() {
  const { instance } = useMsal();
  const [loading, setLoading] = useState(false);
  return (
    <div style={{ minHeight:"100vh", background:"linear-gradient(135deg, #2C497F 0%, #4a6090 50%, #2C497F 100%)", display:"flex", alignItems:"center", justifyContent:"center" }}>
      <div className="fade-in" style={{ textAlign:"center", maxWidth:"380px", width:"90%", padding:"44px 40px", background:"#fff", borderRadius:"20px", boxShadow:"0 24px 80px rgba(0,0,0,0.25)" }}>
        {/* Logo */}
        <div style={{ margin:"0 auto 20px", display:"flex", justifyContent:"center" }}>
          <TechSoftLogo height={60} />
        </div>
        <div style={{ fontSize:"22px", fontWeight:"700", color:C.text, marginBottom:"8px", lineHeight:1.2 }}>Member Timesheet<br/>Portal</div>
        <div style={{ fontSize:"13px", color:C.textMuted, marginBottom:"32px", lineHeight:1.6 }}>
          Sign in with your Microsoft 365 account to log your hours.
        </div>
        <button className="btn btn-primary" disabled={loading}
          onClick={async () => { setLoading(true); try { await instance.loginPopup(loginRequest); } catch { setLoading(false); } }}
          style={{ width:"100%", justifyContent:"center", padding:"12px 24px", fontSize:"14px", borderRadius:"10px" }}>
          <MsIcon /> {loading ? "Signing in…" : "Sign in with Microsoft"}
        </button>
        <div style={{ marginTop:"20px", fontSize:"11px", color:"#A3CCD0" }}>
          Secured by Microsoft Entra ID
        </div>
      </div>
    </div>
  );
}

function MsIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 21 21">
      <rect x="1" y="1" width="9" height="9" fill="#f25022"/>
      <rect x="11" y="1" width="9" height="9" fill="#7fba00"/>
      <rect x="1" y="11" width="9" height="9" fill="#00a4ef"/>
      <rect x="11" y="11" width="9" height="9" fill="#ffb900"/>
    </svg>
  );
}

function TechSoftLogo({ height = 40 }) {
  return (
    <img
      src="/techsoft-logo.jpg"
      alt="Tech Soft Inc."
      style={{
        height:`${height}px`, width:"auto", objectFit:"contain", display:"block",
        border:"2px solid #2C497F",
        borderRadius:"10px",
        padding:"3px",
        background:"#fff",
      }}
    />
  );
}

// ─── Loading Spinner ──────────────────────────────────────────────────────────
function Spinner({ label = "Loading timesheet data…" }) {
  return (
    <div style={{ display:"flex", flexDirection:"column", alignItems:"center", justifyContent:"center", padding:"72px", gap:"14px" }}>
      <div className="spinner" />
      <div style={{ fontSize:"12px", color:C.textMuted }}>{label}</div>
    </div>
  );
}

// ─── Main App ─────────────────────────────────────────────────────────────────
function TimesheetApp() {
  const { accounts, instance } = useMsal();
  const account   = accounts[0];
  const userEmail = account?.username?.toLowerCase() || "";
  const userName  = account?.name || userEmail;
  const isAdmin   = ADMIN_EMAILS.map(e => e.toLowerCase()).includes(userEmail);

  const now = new Date();
  const [viewYear,  setViewYear]  = useState(now.getFullYear());
  const [viewMonth, setViewMonth] = useState(now.getMonth());
  const [activeTab, setActiveTab] = useState(isAdmin ? "summary" : "timesheet");
  const [myData,             setMyData]             = useState({});
  const [myMS,               setMyMS]               = useState({ status: STATUS.DRAFT });
  const [adminAllData,       setAdminAllData]       = useState({});
  const [adminMonthStatuses, setAdminMonthStatuses] = useState({});
  const [allUsers,           setAllUsers]           = useState([]);
  const [loading,            setLoading]            = useState(true);
  const [saved,              setSaved]              = useState(false);
  const [submitConfirm,      setSubmitConfirm]      = useState(false);
  const [apiError,           setApiError]           = useState(null);
  const saveTimer = useRef(null);
  const mk = monthKey(viewYear, viewMonth);
  const isCurrentMonth = viewYear === now.getFullYear() && viewMonth === now.getMonth();

  const loadData = useCallback(async () => {
    setLoading(true); setApiError(null);
    try {
      const email = encodeURIComponent(userEmail);
      const [tsData, msData] = await Promise.all([
        api(`/api/timesheet?email=${email}&month=${mk}`),
        api(`/api/monthstatus?email=${email}&month=${mk}`),
      ]);
      setMyData(tsData || {});
      setMyMS(msData || { status: STATUS.DRAFT });
      if (isAdmin) {
        const adminData = await api(`/api/alldata?month=${mk}`);
        setAdminAllData(adminData.timesheets || {});
        setAdminMonthStatuses(adminData.monthStatuses || {});
        setAllUsers(adminData.users || []);
      }
    } catch (e) {
      setApiError("Could not load data. Check your connection or API configuration.");
    } finally { setLoading(false); }
  }, [userEmail, mk, isAdmin]);

  useEffect(() => { loadData(); }, [loadData]);

  const queueSave = (day, rows) => {
    clearTimeout(saveTimer.current);
    saveTimer.current = setTimeout(() => saveDay(day, rows), 500);
  };

  const saveDay = async (day, rows) => {
    const dk = dateKey(viewYear, viewMonth, day);
    try {
      await api("/api/timesheet", { method:"POST", body:{ email:userEmail, dateKey:dk, rows } });
      setSaved(true); setTimeout(() => setSaved(false), 2000);
    } catch {}
  };

  const days = getDaysInMonth(viewYear, viewMonth);
  const getRows = (day) => myData[dateKey(viewYear, viewMonth, day)] || [];
  const isLocked = myMS.status === STATUS.SUBMITTED || myMS.status === STATUS.APPROVED;

  const hasEntries = () => {
    for (let d = 1; d <= days; d++) {
      if (getRows(d).some(r => r.hours && parseFloat(r.hours) > 0)) return true;
    }
    return false;
  };

  const getLastDefaults = (beforeDay) => {
    for (let d = beforeDay; d >= 1; d--) {
      const rows = getRows(d);
      for (let i = rows.length - 1; i >= 0; i--) {
        if (rows[i].project || rows[i].hours) return { project:rows[i].project||"", hours:rows[i].hours||"" };
      }
    }
    return { project:PROJECT_OPTIONS[0], hours:"8" };
  };

  const addRow = (day) => {
    if (isLocked) return;
    const dk = dateKey(viewYear, viewMonth, day);
    const defaults = getLastDefaults(day);
    const newRow = { id:uid(), project:defaults.project, hours:defaults.hours };
    const newRows = [...(myData[dk]||[]), newRow];
    setMyData(prev => ({ ...prev, [dk]:newRows }));
    queueSave(day, newRows);
  };

  const updateRow = (day, rowId, field, value) => {
    if (isLocked) return;
    const dk = dateKey(viewYear, viewMonth, day);
    const newRows = (myData[dk]||[]).map(r => r.id === rowId ? { ...r, [field]:value } : r);
    setMyData(prev => ({ ...prev, [dk]:newRows }));
    queueSave(day, newRows);
  };

  const removeRow = (day, rowId) => {
    if (isLocked) return;
    const dk = dateKey(viewYear, viewMonth, day);
    const newRows = (myData[dk]||[]).filter(r => r.id !== rowId);
    setMyData(prev => { const u={...prev}; if(!newRows.length) delete u[dk]; else u[dk]=newRows; return u; });
    queueSave(day, newRows);
  };

  const submitTimesheet = async () => {
    const newMS = { status:STATUS.SUBMITTED, submittedBy:userName, submittedAt:new Date().toISOString(), approvedBy:null, approvedAt:null, rejectedBy:null, rejectedAt:null, note:"" };
    try { await api("/api/monthstatus", { method:"POST", body:{ email:userEmail, month:mk, ...newMS } }); setMyMS(newMS); setSubmitConfirm(false); } catch {}
  };

  const recallSubmission = async () => {
    const newMS = { ...myMS, status:STATUS.DRAFT };
    try { await api("/api/monthstatus", { method:"POST", body:{ email:userEmail, month:mk, ...newMS } }); setMyMS(newMS); } catch {}
  };

  const adminApproveMonth = async (email, approve, note) => {
    const prev = adminMonthStatuses[email] || {};
    const newMS = { ...prev, status:approve?STATUS.APPROVED:STATUS.REJECTED, approvedBy:approve?userName:null, approvedAt:approve?new Date().toISOString():null, rejectedBy:!approve?userName:null, rejectedAt:!approve?new Date().toISOString():null, note };
    try {
      await api("/api/monthstatus", { method:"POST", body:{ email, month:mk, ...newMS } });
      setAdminMonthStatuses(p => ({ ...p, [email]:newMS }));
      const emailData = adminAllData[email] || {};
      const updates = {};
      const promises = Object.entries(emailData).map(([dk, rows]) => {
        const updatedRows = rows.map(r => ({ ...r, lineStatus:approve?STATUS.APPROVED:STATUS.REJECTED, lineApprovedBy:approve?userName:null, lineRejectedBy:!approve?userName:null }));
        updates[dk] = updatedRows;
        return api("/api/timesheet", { method:"POST", body:{ email, dateKey:dk, rows:updatedRows } });
      });
      await Promise.all(promises);
      setAdminAllData(p => ({ ...p, [email]:{ ...emailData, ...updates } }));
    } catch {}
  };

  const adminApproveLine = async (email, day, rowId, approve, note) => {
    const dk = dateKey(viewYear, viewMonth, day);
    const rows = (adminAllData[email]||{})[dk] || [];
    const updatedRows = rows.map(r => r.id === rowId ? { ...r, lineStatus:approve?STATUS.APPROVED:STATUS.REJECTED, lineApprovedBy:approve?userName:null, lineRejectedBy:!approve?userName:null, lineNote:note } : r);
    try {
      await api("/api/timesheet", { method:"POST", body:{ email, dateKey:dk, rows:updatedRows } });
      setAdminAllData(p => ({ ...p, [email]:{ ...(p[email]||{}), [dk]:updatedRows } }));
    } catch {}
  };

  const prevMonth = () => { if(viewMonth===0){setViewYear(y=>y-1);setViewMonth(11);}else setViewMonth(m=>m-1); };
  const nextMonth = () => { if(viewMonth===11){setViewYear(y=>y+1);setViewMonth(0);}else setViewMonth(m=>m+1); };
  const jumpToCurrentMonth = () => { setViewYear(now.getFullYear()); setViewMonth(now.getMonth()); };

  const monthTotal = () => { let t=0; for(let d=1;d<=days;d++) getRows(d).forEach(r=>{const h=parseFloat(r.hours);if(!isNaN(h))t+=h;}); return t; };
  const projectTotal = (p) => { let t=0; for(let d=1;d<=days;d++) getRows(d).forEach(r=>{if(r.project===p){const h=parseFloat(r.hours);if(!isNaN(h))t+=h;}}); return t; };

  const exportCSV = () => {
    const source = isAdmin ? adminAllData : { [userEmail]:myData };
    const rows = [["Email","Date","Day","Project","Hours","Month Status"]];
    Object.entries(source).forEach(([email,data]) => {
      const ms=(isAdmin?adminMonthStatuses[email]?.status:myMS.status)||STATUS.DRAFT;
      for(let d=1;d<=days;d++){const dk=dateKey(viewYear,viewMonth,d);(data[dk]||[]).forEach(r=>{if(r.hours)rows.push([email,dk,getDayName(viewYear,viewMonth,d),r.project||"",r.hours,ms]);});}
    });
    const csv=rows.map(r=>r.map(v=>`"${v}"`).join(",")).join("\n");
    const blob=new Blob([csv],{type:"text/csv"});
    const a=document.createElement("a");a.href=URL.createObjectURL(blob);
    a.download=`timesheet-${viewYear}-${pad(viewMonth+1)}.csv`;a.click();
  };

  const todayStr = dateKey(now.getFullYear(), now.getMonth(), now.getDate());

  return (
    <div style={{ minHeight:"100vh", background:C.bg, color:C.text }}>

      {/* ── HEADER ── */}
      <div style={{ background:"#ffffff", borderBottom:"2px solid #2C497F", boxShadow:"0 2px 12px rgba(44,73,127,0.1)" }}>
        {/* Top bar — logo + nav + user */}
        <div style={{ padding:"10px 28px", display:"flex", alignItems:"center", justifyContent:"space-between", flexWrap:"wrap", gap:"10px" }}>

          {/* Tech Soft Logo */}
          <div style={{ display:"flex", alignItems:"center", gap:"14px" }}>
            <TechSoftLogo height={60} />
            <div style={{ width:"1px", height:"36px", background:"#A3CCD0" }} />
            <div>
              <div style={{ fontSize:"13px", color:"#2C497F", fontWeight:"600" }}>Member Timesheet Portal</div>
              <div style={{ fontSize:"10px", color:"#A6A6A6", letterSpacing:"0.04em" }}>Time Tracking & Approval</div>
            </div>
          </div>

          {/* Right side — user + actions */}
          <div style={{ display:"flex", alignItems:"center", gap:"10px" }}>
            {saved && (
              <span className="saved-badge" style={{ background:"#E8F3F2", color:"#2C497F", padding:"3px 12px", borderRadius:"20px", fontSize:"11px", fontWeight:"700", border:"1px solid #A3CCD0" }}>✓ Saved</span>
            )}
            <div style={{ textAlign:"right" }}>
              <div style={{ fontSize:"12px", color:"#2C497F", fontWeight:"600" }}>{userName}</div>
              <div style={{ fontSize:"10px", color:"#A6A6A6" }}>{userEmail}</div>
            </div>
            {isAdmin && (
              <span style={{ background:"#2C497F", color:"#fff", padding:"3px 10px", borderRadius:"5px", fontSize:"10px", fontWeight:"700", letterSpacing:"0.06em" }}>ADMIN</span>
            )}
            <div style={{ width:"1px", height:"28px", background:"#A3CCD0" }} />
            <button onClick={exportCSV}
              style={{ background:"#E8F3F2", border:"1.5px solid #A3CCD0", color:"#2C497F", padding:"6px 14px", borderRadius:"7px", cursor:"pointer", fontFamily:"'DM Sans', sans-serif", fontSize:"12px", fontWeight:"600", display:"flex", alignItems:"center", gap:"5px", transition:"all 0.15s" }}
              onMouseOver={e=>{e.currentTarget.style.background="#d4ecf0";e.currentTarget.style.borderColor="#2C497F";}}
              onMouseOut={e=>{e.currentTarget.style.background="#E8F3F2";e.currentTarget.style.borderColor="#A3CCD0";}}>
              Export CSV
            </button>
            <button onClick={() => instance.logoutPopup()}
              style={{ background:"transparent", border:"1.5px solid #A3CCD0", color:"#A6A6A6", padding:"6px 14px", borderRadius:"7px", cursor:"pointer", fontFamily:"'DM Sans', sans-serif", fontSize:"12px", fontWeight:"500", transition:"all 0.15s" }}
              onMouseOver={e=>{e.currentTarget.style.borderColor="#2C497F";e.currentTarget.style.color="#2C497F";}}
              onMouseOut={e=>{e.currentTarget.style.borderColor="#A3CCD0";e.currentTarget.style.color="#A6A6A6";}}>
              Sign Out
            </button>
          </div>
        </div>

        {/* Sub-bar — month navigator + total */}
        <div style={{ background:"#EDF6F9", borderTop:"1px solid #A3CCD0", padding:"8px 28px", display:"flex", alignItems:"center", gap:"12px", flexWrap:"wrap" }}>

          {/* Month navigator */}
          <div style={{ display:"flex", alignItems:"center", gap:"2px", background:"#fff", border:"1px solid #A3CCD0", borderRadius:"8px", padding:"3px", boxShadow:"0 1px 3px rgba(44,73,127,0.06)" }}>
            <button onClick={prevMonth}
              style={{ background:"transparent", border:"none", color:"#2C497F", cursor:"pointer", fontSize:"16px", width:"30px", height:"30px", borderRadius:"6px", display:"flex", alignItems:"center", justifyContent:"center", transition:"all 0.15s" }}
              onMouseOver={e=>{e.currentTarget.style.background="#E8F3F2";}}
              onMouseOut={e=>{e.currentTarget.style.background="transparent";}}>‹</button>
            <span style={{ fontSize:"14px", fontWeight:"700", color:"#2C497F", minWidth:"148px", textAlign:"center", letterSpacing:"-0.01em" }}>
              {MONTH_NAMES[viewMonth]} {viewYear}
            </span>
            <button onClick={nextMonth}
              style={{ background:"transparent", border:"none", color:"#2C497F", cursor:"pointer", fontSize:"16px", width:"30px", height:"30px", borderRadius:"6px", display:"flex", alignItems:"center", justifyContent:"center", transition:"all 0.15s" }}
              onMouseOver={e=>{e.currentTarget.style.background="#E8F3F2";}}
              onMouseOut={e=>{e.currentTarget.style.background="transparent";}}>›</button>
          </div>

          {/* Jump to current month */}
          <button onClick={jumpToCurrentMonth} disabled={isCurrentMonth}
            style={{
              background: isCurrentMonth ? "#EDF6F9" : "#2C497F",
              color: isCurrentMonth ? "#A6A6A6" : "#ffffff",
              border: `1.5px solid ${isCurrentMonth ? "#A3CCD0" : "#2C497F"}`,
              padding:"6px 16px", borderRadius:"7px", fontSize:"12px", fontWeight:"600",
              cursor: isCurrentMonth ? "default" : "pointer",
              fontFamily:"'DM Sans', sans-serif", transition:"all 0.15s",
            }}
            onMouseOver={e=>{ if(!isCurrentMonth){e.currentTarget.style.background="#3355a0";} }}
            onMouseOut={e=>{ if(!isCurrentMonth){e.currentTarget.style.background="#2C497F";} }}>
            {isCurrentMonth ? "Current Month" : "Jump to Current Month"}
          </button>

          {/* Divider */}
          <div style={{ width:"1px", height:"24px", background:"#A3CCD0" }} />

          {/* Month total */}
          {activeTab === "timesheet" && !loading && (
            <div style={{ display:"flex", alignItems:"center", gap:"8px" }}>
              <span style={{ fontSize:"11px", color:"#A6A6A6", fontWeight:"600", letterSpacing:"0.06em" }}>MONTH TOTAL</span>
              <span style={{ fontSize:"20px", fontWeight:"700", color:"#2C497F", fontFamily:"'DM Mono', monospace", lineHeight:1 }}>
                {monthTotal().toFixed(1)}
              </span>
              <span style={{ fontSize:"11px", color:"#A6A6A6" }}>hrs</span>
            </div>
          )}

          {/* Tabs pushed to right */}
          {isAdmin && (
            <div style={{ marginLeft:"auto", display:"flex", gap:"4px", background:"#EDF6F9", border:"1.5px solid #A3CCD0", borderRadius:"8px", padding:"3px" }}>
              <button onClick={() => setActiveTab("timesheet")}
                style={{ padding:"5px 16px", borderRadius:"6px", cursor:"pointer", fontFamily:"'DM Sans', sans-serif", fontSize:"12px", fontWeight:"600", transition:"all 0.15s", background:activeTab==="timesheet"?"#2C497F":"transparent", color:activeTab==="timesheet"?"#fff":"#A6A6A6", border:"none" }}>
                My Timesheet
              </button>
              <button onClick={() => setActiveTab("summary")}
                style={{ padding:"5px 16px", borderRadius:"6px", cursor:"pointer", fontFamily:"'DM Sans', sans-serif", fontSize:"12px", fontWeight:"600", transition:"all 0.15s", background:activeTab==="summary"?"#2C497F":"transparent", color:activeTab==="summary"?"#fff":"#A6A6A6", border:"none" }}>
                All Members
              </button>
            </div>
          )}
        </div>
      </div>

      {/* ── ERROR BANNER ── */}
      {apiError && (
        <div style={{ background:C.rejectedBg, border:`1px solid ${C.rejectedBorder}`, borderRadius:"8px", margin:"16px 28px", padding:"12px 16px", fontSize:"12px", color:C.rejected, display:"flex", alignItems:"center", justifyContent:"space-between" }}>
          <span>⚠ {apiError}</span>
          <button className="btn btn-reject" onClick={loadData} style={{ padding:"4px 14px" }}>Retry</button>
        </div>
      )}

      {/* ── SUBMIT CONFIRM MODAL ── */}
      {submitConfirm && (
        <div className="modal-backdrop">
          <div className="modal-card">
            <div style={{ fontSize:"32px", textAlign:"center", marginBottom:"12px" }}>📤</div>
            <div style={{ fontSize:"17px", fontWeight:"700", color:C.text, marginBottom:"8px", textAlign:"center" }}>Submit for Approval?</div>
            <div style={{ fontSize:"13px", color:C.textMid, marginBottom:"8px", textAlign:"center" }}>
              <strong>{MONTH_NAMES[viewMonth]} {viewYear}</strong> — {monthTotal().toFixed(1)} hours total
            </div>
            <div style={{ fontSize:"12px", color:C.textMuted, background:C.surfaceAlt, border:`1px solid ${C.border}`, borderRadius:"8px", padding:"12px 14px", marginBottom:"24px", lineHeight:1.6 }}>
              ⚠ Once submitted, you <strong>cannot edit</strong> your timesheet until your supervisor reviews it.
            </div>
            <div style={{ display:"flex", gap:"10px", justifyContent:"flex-end" }}>
              <button className="btn btn-recall" onClick={() => setSubmitConfirm(false)}>Cancel</button>
              <button className="btn btn-submit" onClick={submitTimesheet}>📤 Submit for Approval</button>
            </div>
          </div>
        </div>
      )}

      {/* ── TIMESHEET ── */}
      {activeTab === "timesheet" && (
        loading ? <Spinner /> : (
        <div className="fade-in" style={{ maxWidth:"780px", margin:"0 auto", padding:"20px 24px" }}>

          {/* Status Banner */}
          <div style={{
            display:"flex", alignItems:"center", justifyContent:"space-between",
            marginBottom:"16px", padding:"14px 18px", borderRadius:"10px", flexWrap:"wrap", gap:"10px",
            background: myMS.status===STATUS.APPROVED?C.approvedBg : myMS.status===STATUS.SUBMITTED?C.submittedBg : myMS.status===STATUS.REJECTED?C.rejectedBg : C.surface,
            border:`1.5px solid ${myMS.status===STATUS.APPROVED?C.approvedBorder : myMS.status===STATUS.SUBMITTED?C.submittedBorder : myMS.status===STATUS.REJECTED?C.rejectedBorder : C.border}`,
            boxShadow:"0 1px 4px rgba(0,0,0,0.04)",
          }}>
            <div style={{ display:"flex", flexDirection:"column", gap:"8px" }}>
              <div style={{ display:"flex", alignItems:"center", gap:"10px", flexWrap:"wrap" }}>
                <StatusPill status={myMS.status || STATUS.DRAFT} />
                <span style={{ fontSize:"13px", color:C.textMid, fontWeight:"600" }}>{MONTH_NAMES[viewMonth]} {viewYear} Timesheet</span>
                {myMS.submittedAt && myMS.status !== STATUS.DRAFT && (
                  <span style={{ fontSize:"11px", color:C.textMuted }}>Submitted {fmtDt(myMS.submittedAt)}</span>
                )}
              </div>
              {myMS.status === STATUS.APPROVED && (
                <div style={{ display:"inline-flex", alignItems:"center", gap:"8px", background:"#fff", border:`1.5px solid ${C.approvedBorder}`, borderRadius:"8px", padding:"6px 12px", width:"fit-content" }}>
                  <span style={{ fontSize:"14px", color:"#2C497F", fontWeight:"700" }}>✓</span>
                  <div>
                    <div style={{ fontSize:"12px", color:C.approved, fontWeight:"700" }}>Approved by {myMS.approvedBy}</div>
                    <div style={{ fontSize:"10px", color:C.textMuted }}>{fmtDt(myMS.approvedAt)}</div>
                  </div>
                </div>
              )}
              {myMS.status === STATUS.REJECTED && (
                <div style={{ display:"inline-flex", alignItems:"flex-start", gap:"8px", background:"#fff", border:`1.5px solid ${C.rejectedBorder}`, borderRadius:"8px", padding:"6px 12px", width:"fit-content" }}>
                  <span style={{ fontSize:"16px" }}>🚫</span>
                  <div>
                    <div style={{ fontSize:"12px", color:C.rejected, fontWeight:"700" }}>Rejected by {myMS.rejectedBy}</div>
                    <div style={{ fontSize:"10px", color:C.textMuted }}>{fmtDt(myMS.rejectedAt)}</div>
                    {myMS.note && <div style={{ fontSize:"11px", color:C.rejected, marginTop:"3px", fontStyle:"italic" }}>"{myMS.note}"</div>}
                  </div>
                </div>
              )}
            </div>
            <div style={{ display:"flex", gap:"8px", alignItems:"center" }}>
              {(!myMS.status || myMS.status === STATUS.DRAFT) && (
                <button className="btn btn-submit" onClick={() => hasEntries() ? setSubmitConfirm(true) : alert("Please add at least one time entry before submitting.")}>
                  📤 Submit for Approval
                </button>
              )}
              {myMS.status === STATUS.SUBMITTED && (
                <>
                  <span style={{ fontSize:"11px", color:C.submitted, fontWeight:"500" }}>⏳ Awaiting supervisor review</span>
                  <button className="btn btn-recall" onClick={recallSubmission}>↩ Recall & Edit</button>
                </>
              )}
              {myMS.status === STATUS.APPROVED && <span style={{ fontSize:"11px", color:C.approved, fontWeight:"700" }}>🔒 Locked</span>}
              {myMS.status === STATUS.REJECTED && (
                <button className="btn btn-submit" onClick={() => setSubmitConfirm(true)}>📤 Resubmit</button>
              )}
            </div>
          </div>

          {/* Column headers — desktop only */}
          <div className="ts-grid-header" style={{ display:"grid", gridTemplateColumns:"80px 1fr 80px 70px", gap:"6px", padding:"0 10px", marginBottom:"6px" }}>
            {["DAY / DATE","PROJECT","HOURS",""].map((h,i) => (
              <div key={i} style={{ fontSize:"9px", color:C.textMuted, letterSpacing:"0.12em", fontWeight:"700" }}>{h}</div>
            ))}
          </div>

          {/* Day rows */}
          <div style={{ display:"flex", flexDirection:"column", gap:"3px" }}>
            {Array.from({ length:days }, (_,i) => i+1).map(day => {
              const dk      = dateKey(viewYear, viewMonth, day);
              const weekend = isWeekend(viewYear, viewMonth, day);
              const isToday = dk === todayStr;
              const rows    = getRows(day);
              const dayHrs  = rows.reduce((t,r)=>{const h=parseFloat(r.hours);return t+(isNaN(h)?0:h);},0);
              const rowBg   = isToday ? C.today : weekend ? C.weekend : C.surface;
              const rowBord = isToday ? C.todayBorder : weekend ? C.weekendBorder : C.borderLight;
              const rowClass = `day-row${isToday?" today":weekend?" weekend":""}`;

              return (
                <div key={day} className={rowClass} style={{ borderRadius:"8px", border:`1.5px solid ${rowBord}`, background:rowBg, overflow:"hidden", boxShadow:"0 1px 2px rgba(0,0,0,0.03)" }}>
                  {rows.length === 0 ? (
                    <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", padding:"6px 10px", gap:"8px" }}>
                      <div style={{ display:"flex", alignItems:"center", gap:"8px", minWidth:0 }}>
                        <DayCell day={day} dayName={getDayName(viewYear,viewMonth,day)} isToday={isToday} weekend={weekend} />
                        <div style={{ fontSize:"11px", color:C.textMuted, whiteSpace:"nowrap" }}>{MONTH_NAMES[viewMonth].slice(0,3)} {day}</div>
                      </div>
                      {!isLocked && (
                        <button className="btn btn-ghost" onClick={() => addRow(day)} style={{ whiteSpace:"nowrap", padding:"5px 14px", fontSize:"12px", flexShrink:0 }}>+ Add</button>
                      )}
                    </div>
                  ) : rows.map((row, idx) => {
                    const lineLocked = isLocked || row.lineStatus === STATUS.APPROVED;
                    return (
                      <div key={row.id} style={{ padding:"6px 10px", borderTop:idx>0?`1px solid ${C.borderLight}`:"none" }}>
                        {/* Day + Date — only on first row */}
                        {idx === 0 && (
                          <div style={{ display:"flex", alignItems:"center", gap:"8px", marginBottom:"6px" }}>
                            <DayCell day={day} dayName={getDayName(viewYear,viewMonth,day)} isToday={isToday} weekend={weekend}/>
                            <div style={{ fontSize:"11px", color:C.textMuted }}>{MONTH_NAMES[viewMonth].slice(0,3)} {day}</div>
                            {idx > 0 && <span style={{ fontSize:"10px", color:C.textMuted, fontStyle:"italic" }}>↳ additional entry</span>}
                          </div>
                        )}
                        {idx > 0 && (
                          <div style={{ fontSize:"10px", color:C.textMuted, fontStyle:"italic", marginBottom:"5px" }}>↳ additional entry</div>
                        )}

                        {/* Project + Hours + Actions on one line */}
                        <div style={{ display:"flex", gap:"6px", alignItems:"center" }}>
                          {/* Project select */}
                          <select className="ts-select" value={row.project||""} disabled={lineLocked}
                            onChange={e => updateRow(day,row.id,"project",e.target.value)}
                            style={{ flex:2, background:row.project?(PROJECT_COLORS[row.project]?.bg||C.surface):C.surfaceAlt, border:`1.5px solid ${row.project?(PROJECT_COLORS[row.project]?.border||C.border):C.border}`, color:row.project?(PROJECT_COLORS[row.project]?.color||C.text):C.textMuted, fontWeight:row.project?"600":"400" }}>
                            <option value="">— select project —</option>
                            {PROJECT_OPTIONS.map(p => <option key={p} value={p}>{p}</option>)}
                          </select>

                          {/* Hours input */}
                          <input className="ts-input" type="number" step="0.5" min="0" max="24" placeholder="0.0"
                            value={row.hours||""} disabled={lineLocked}
                            onChange={e => updateRow(day,row.id,"hours",e.target.value)}
                            style={{ flex:1, minWidth:"60px", background:row.hours?C.successBg:C.surfaceAlt, border:`1.5px solid ${row.hours?"#A3CCD0":C.border}`, color:row.hours?"#2C497F":C.textMuted, fontWeight:row.hours?"700":"400" }}
                          />

                          {/* Actions */}
                          {!lineLocked && idx===rows.length-1 ? (
                            <div style={{ display:"flex", gap:"4px", flexShrink:0 }}>
                              <button className="btn btn-ghost" onClick={() => addRow(day)} title="Add row" style={{ padding:"5px 10px", fontSize:"14px" }}>+</button>
                              <button className="btn btn-danger" onClick={() => removeRow(day,row.id)} title="Remove" style={{ padding:"5px 10px", fontSize:"13px" }}>✕</button>
                            </div>
                          ) : !lineLocked ? (
                            <button className="btn btn-danger" onClick={() => removeRow(day,row.id)} title="Remove" style={{ padding:"5px 10px", fontSize:"13px", flexShrink:0 }}>✕</button>
                          ) : (
                            <div style={{ textAlign:"center", fontSize:"13px", color:C.textMuted, flexShrink:0, width:"24px" }}>🔒</div>
                          )}
                        </div>

                        {/* Approver name */}
                        {(row.lineStatus===STATUS.APPROVED && row.lineApprovedBy) && (
                          <div style={{ fontSize:"10px", color:"#2C497F", fontWeight:"700", marginTop:"4px" }}>✓ Approved by {row.lineApprovedBy}</div>
                        )}
                        {(row.lineStatus===STATUS.REJECTED && row.lineRejectedBy) && (
                          <div style={{ fontSize:"10px", color:C.rejected, fontWeight:"700", marginTop:"4px" }}>✕ Rejected by {row.lineRejectedBy}</div>
                        )}
                      </div>
                    );
                  })}

                  {rows.length > 1 && dayHrs > 0 && (
                    <div style={{ padding:"3px 10px 5px", display:"flex", justifyContent:"flex-end", borderTop:`1px solid ${C.borderLight}`, background:"rgba(0,0,0,0.01)" }}>
                      <span style={{ fontSize:"10px", color:C.textMuted }}>Day total:&nbsp;</span>
                      <span style={{ fontSize:"10px", color:C.accent, fontWeight:"700", fontFamily:"'DM Mono', monospace" }}>{dayHrs.toFixed(1)} hrs</span>
                    </div>
                  )}
                </div>
              );
            })}
          </div>

          {/* Monthly breakdown footer */}
          <div style={{ marginTop:"20px", padding:"16px 20px", background:C.surface, border:`1.5px solid ${C.border}`, borderRadius:"12px", boxShadow:"0 2px 8px rgba(0,0,0,0.05)" }}>
            <div style={{ display:"flex", justifyContent:"space-between", alignItems:"flex-start", flexWrap:"wrap", gap:"12px" }}>
              <div style={{ flex:1 }}>
                <div style={{ fontSize:"10px", color:C.accent, letterSpacing:"0.12em", fontWeight:"700", marginBottom:"12px" }}>
                  {MONTH_NAMES[viewMonth].toUpperCase()} {viewYear} — BREAKDOWN
                </div>
                <div style={{ display:"flex", flexWrap:"wrap", gap:"8px", alignItems:"center" }}>
                  {PROJECT_OPTIONS.map(p => {
                    const t=projectTotal(p); if(!t) return null;
                    const pc=PROJECT_COLORS[p];
                    return (
                      <div key={p} style={{ display:"flex", alignItems:"center", gap:"7px", background:pc.bg, border:`1.5px solid ${pc.border}`, borderRadius:"8px", padding:"6px 12px" }}>
                        <span style={{ color:pc.color, fontSize:"11px", fontWeight:"700" }}>{p}</span>
                        <span style={{ color:C.text, fontSize:"14px", fontWeight:"700", fontFamily:"'DM Mono', monospace" }}>{t.toFixed(1)}</span>
                        <span style={{ color:C.textMuted, fontSize:"10px" }}>hrs</span>
                      </div>
                    );
                  })}
                  <div style={{ marginLeft:"auto", display:"flex", alignItems:"center", gap:"8px", borderLeft:`1.5px solid ${C.border}`, paddingLeft:"14px" }}>
                    <span style={{ color:C.textMuted, fontSize:"11px", fontWeight:"600" }}>TOTAL</span>
                    <span style={{ color:C.accent, fontSize:"20px", fontWeight:"700", fontFamily:"'DM Mono', monospace" }}>{monthTotal().toFixed(1)}</span>
                    <span style={{ color:C.textMuted, fontSize:"11px" }}>hrs</span>
                  </div>
                </div>
              </div>
              {(!myMS.status || myMS.status===STATUS.DRAFT) && (
                <button className="btn btn-submit" onClick={() => hasEntries() ? setSubmitConfirm(true) : alert("Please add at least one time entry before submitting.")} style={{ alignSelf:"flex-end", padding:"10px 22px", fontSize:"13px" }}>
                  📤 Submit for Approval
                </button>
              )}
              {myMS.status===STATUS.REJECTED && (
                <button className="btn btn-submit" onClick={() => setSubmitConfirm(true)} style={{ alignSelf:"flex-end", padding:"10px 22px", fontSize:"13px" }}>
                  📤 Resubmit for Approval
                </button>
              )}
            </div>
          </div>
        </div>
        )
      )}

      {/* ── ADMIN SUMMARY ── */}
      {activeTab === "summary" && isAdmin && (
        loading ? <Spinner /> : (
          <AdminSummary
            timesheets={adminAllData} monthStatuses={adminMonthStatuses}
            allUsers={allUsers} viewYear={viewYear} viewMonth={viewMonth} days={days}
            onApproveMonth={adminApproveMonth} onApproveLine={adminApproveLine}
          />
        )
      )}
    </div>
  );
}

// ─── Day Cell ─────────────────────────────────────────────────────────────────
function DayCell({ day, dayName, isToday, weekend }) {
  return (
    <div style={{ textAlign:"center" }}>
      {isToday ? (
        <span style={{ background:C.accent, color:"#fff", borderRadius:"50%", width:"24px", height:"24px", display:"inline-flex", alignItems:"center", justifyContent:"center", fontSize:"10px", fontWeight:"700", boxShadow:"0 2px 6px rgba(44,73,127,0.4)" }}>
          {dayName.slice(0,2)}
        </span>
      ) : (
        <span style={{ fontSize:"11px", color:weekend?"#2C497F":C.textMid, fontWeight:weekend?"700":"500" }}>{dayName}</span>
      )}
    </div>
  );
}

// ─── Admin Summary ────────────────────────────────────────────────────────────
function AdminSummary({ timesheets, monthStatuses, allUsers, viewYear, viewMonth, days, onApproveMonth, onApproveLine }) {
  const [modal, setModal] = useState(null);

  const getSummary = (email) => {
    const data=timesheets[email]||{};
    const byProject={}; let total=0;
    for(let d=1;d<=days;d++){
      (data[dateKey(viewYear,viewMonth,d)]||[]).forEach(r=>{
        const h=parseFloat(r.hours);
        if(!isNaN(h)&&h>0){const p=r.project||"UNASSIGNED";byProject[p]=(byProject[p]||0)+h;total+=h;}
      });
    }
    return {byProject,total};
  };

  const combined={}; let grandTotal=0;
  allUsers.forEach(e=>{const s=getSummary(e);combined[e]=s;grandTotal+=s.total;});
  const projectTotals={};
  Object.values(combined).forEach(({byProject})=>Object.entries(byProject).forEach(([p,h])=>{projectTotals[p]=(projectTotals[p]||0)+h;}));

  const doApprove=(note)=>{
    if(!modal)return;
    if(modal.type==="month")onApproveMonth(modal.email,modal.approve,note);
    else onApproveLine(modal.email,modal.day,modal.rowId,modal.approve,note);
    setModal(null);
  };

  const pendingCount=allUsers.filter(e=>monthStatuses[e]?.status===STATUS.SUBMITTED).length;

  return (
    <div className="fade-in" style={{ maxWidth:"960px", margin:"0 auto", padding:"20px 24px" }}>
      {modal && <ApprovalModal action={modal.approve?"Approve":"Reject"} target={modal.type==="month"?`${MONTH_NAMES[viewMonth]} ${viewYear} for ${modal.email}`:`entry for ${modal.email}`} onConfirm={doApprove} onCancel={()=>setModal(null)} />}

      {/* Grand totals */}
      <div style={{ marginBottom:"16px", padding:"16px 20px", background:C.surface, border:`1.5px solid ${C.border}`, borderRadius:"12px", boxShadow:"0 2px 8px rgba(0,0,0,0.05)" }}>
        <div style={{ fontSize:"10px", color:C.accent, letterSpacing:"0.12em", fontWeight:"700", marginBottom:"12px" }}>
          {MONTH_NAMES[viewMonth].toUpperCase()} {viewYear} — ALL MEMBERS
        </div>
        <div style={{ display:"flex", flexWrap:"wrap", gap:"8px", alignItems:"center" }}>
          {PROJECT_OPTIONS.map(p=>{
            const t=projectTotals[p]||0; if(!t)return null;
            const pc=PROJECT_COLORS[p];
            return (
              <div key={p} style={{ display:"flex", alignItems:"center", gap:"7px", background:pc.bg, border:`1.5px solid ${pc.border}`, borderRadius:"8px", padding:"6px 12px" }}>
                <span style={{ color:pc.color, fontSize:"11px", fontWeight:"700" }}>{p}</span>
                <span style={{ color:C.text, fontSize:"14px", fontWeight:"700", fontFamily:"'DM Mono', monospace" }}>{t.toFixed(1)}</span>
                <span style={{ color:C.textMuted, fontSize:"10px" }}>hrs</span>
              </div>
            );
          })}
          <div style={{ marginLeft:"auto", display:"flex", alignItems:"center", gap:"8px", borderLeft:`1.5px solid ${C.border}`, paddingLeft:"14px" }}>
            <span style={{ color:C.textMuted, fontSize:"11px", fontWeight:"600" }}>GRAND TOTAL</span>
            <span style={{ color:C.accent, fontSize:"22px", fontWeight:"700", fontFamily:"'DM Mono', monospace" }}>{grandTotal.toFixed(1)}</span>
            <span style={{ color:C.textMuted, fontSize:"11px" }}>hrs</span>
          </div>
        </div>
      </div>

      {/* Pending alert */}
      {pendingCount > 0 && (
        <div style={{ marginBottom:"14px", padding:"12px 16px", background:C.submittedBg, border:`1.5px solid ${C.submittedBorder}`, borderRadius:"8px", fontSize:"13px", color:C.submitted, fontWeight:"600", display:"flex", alignItems:"center", gap:"8px" }}>
          <span style={{ fontSize:"18px" }}>📤</span>
          {pendingCount} member timesheet{pendingCount>1?"s":""} awaiting your approval this month.
        </div>
      )}

      <div style={{ fontSize:"10px", color:C.textMuted, letterSpacing:"0.12em", fontWeight:"700", marginBottom:"10px" }}>BY MEMBER</div>

      {allUsers.length===0 && (
        <div style={{ color:C.textMuted, textAlign:"center", padding:"60px", background:C.surface, borderRadius:"12px", border:`1.5px solid ${C.border}`, fontSize:"13px" }}>
          No entries logged for this month yet.
        </div>
      )}

      {allUsers.map(email=>{
        const {byProject,total}=combined[email]||{byProject:{},total:0};
        const ms=monthStatuses[email]||{status:STATUS.DRAFT};
        const isSubmitted=ms.status===STATUS.SUBMITTED;
        const isApproved=ms.status===STATUS.APPROVED;
        const isRejected=ms.status===STATUS.REJECTED;
        const isDraft=!ms.status||ms.status===STATUS.DRAFT;
        if(!total&&isDraft)return null;

        return (
          <div key={email} className="sub-card" style={{ background:C.surface, border:`2px solid ${isSubmitted?C.submittedBorder:isApproved?C.approvedBorder:isRejected?C.rejectedBorder:C.border}`, borderRadius:"12px", marginBottom:"12px", overflow:"hidden", boxShadow:"0 1px 4px rgba(0,0,0,0.05)" }}>

            {/* Sub header */}
            <div style={{ padding:"12px 18px", background:isSubmitted?C.submittedBg:isApproved?C.approvedBg:isRejected?C.rejectedBg:C.surfaceAlt, borderBottom:`1px solid ${C.border}`, display:"flex", justifyContent:"space-between", alignItems:"center", flexWrap:"wrap", gap:"10px" }}>
              <div style={{ display:"flex", alignItems:"center", gap:"10px", flexWrap:"wrap" }}>
                <div style={{ fontWeight:"700", color:C.text, fontSize:"14px" }}>{email}</div>
                {total>0 && <span style={{ color:C.accent, fontWeight:"700", fontSize:"14px", fontFamily:"'DM Mono', monospace" }}>{total.toFixed(1)} hrs</span>}
                <StatusPill status={ms.status||STATUS.DRAFT} />
                {ms.submittedAt && <span style={{ fontSize:"11px", color:C.textMuted }}>Submitted {fmtDt(ms.submittedAt)}</span>}
                {isApproved && ms.approvedBy && (
                  <span style={{ display:"inline-flex", alignItems:"center", gap:"5px", background:"#fff", border:`1.5px solid ${C.approvedBorder}`, borderRadius:"6px", padding:"3px 10px" }}>
                    <span style={{ fontSize:"13px", color:"#2C497F", fontWeight:"700" }}>✓</span>
                    <span style={{ fontSize:"12px", color:C.approved, fontWeight:"700" }}>Approved by {ms.approvedBy}</span>
                    <span style={{ fontSize:"10px", color:C.textMuted }}>· {fmtDt(ms.approvedAt)}</span>
                  </span>
                )}
                {isRejected && ms.rejectedBy && (
                  <span style={{ display:"inline-flex", alignItems:"center", gap:"5px", background:"#fff", border:`1.5px solid ${C.rejectedBorder}`, borderRadius:"6px", padding:"3px 10px" }}>
                    <span>🚫</span>
                    <span style={{ fontSize:"12px", color:C.rejected, fontWeight:"700" }}>Rejected by {ms.rejectedBy}</span>
                    {ms.note && <span style={{ fontSize:"10px", color:C.rejected, fontStyle:"italic" }}>— "{ms.note}"</span>}
                  </span>
                )}
              </div>
              {(isSubmitted||isApproved||isRejected) && (
                <div style={{ display:"flex", gap:"6px", alignItems:"center" }}>
                  <span style={{ fontSize:"11px", color:C.textMuted, fontWeight:"500" }}>Month:</span>
                  <button className={`btn btn-approve${isApproved?" active":""}`} onClick={()=>setModal({email,type:"month",approve:true})}>✓ Approve All</button>
                  <button className={`btn btn-reject${isRejected?" active":""}`} onClick={()=>setModal({email,type:"month",approve:false})}>✕ Reject</button>
                </div>
              )}
              {isDraft && <span style={{ fontSize:"11px", color:C.textMuted, fontStyle:"italic" }}>{total>0?"In progress — not submitted":"Not yet submitted"}</span>}
            </div>

            {/* Project breakdown */}
            {total>0 && (
              <div style={{ padding:"12px 18px", display:"flex", flexWrap:"wrap", gap:"8px", borderBottom:`1px solid ${C.borderLight}` }}>
                {Object.entries(byProject).map(([p,h])=>{
                  const pc=PROJECT_COLORS[p]||{bg:C.surfaceAlt,color:C.textMid,border:C.border};
                  const pct=total>0?((h/total)*100).toFixed(0):0;
                  return (
                    <div key={p} style={{ display:"flex", alignItems:"center", gap:"8px", background:pc.bg, border:`1.5px solid ${pc.border}`, borderRadius:"8px", padding:"6px 12px" }}>
                      <span style={{ color:pc.color, fontSize:"11px", fontWeight:"700", minWidth:"56px" }}>{p}</span>
                      <div style={{ width:"60px", height:"5px", background:"rgba(0,0,0,0.08)", borderRadius:"3px", overflow:"hidden" }}>
                        <div style={{ width:`${pct}%`, height:"100%", background:pc.color, borderRadius:"3px", opacity:0.7 }} />
                      </div>
                      <span style={{ color:C.text, fontWeight:"700", fontSize:"13px", fontFamily:"'DM Mono', monospace" }}>{h.toFixed(1)}</span>
                      <span style={{ color:C.textMuted, fontSize:"10px" }}>hrs · {pct}%</span>
                    </div>
                  );
                })}
              </div>
            )}

            {(isSubmitted||isApproved||isRejected)&&total>0 && (
              <LineApprovalTable email={email} timesheets={timesheets} viewYear={viewYear} viewMonth={viewMonth} days={days}
                onApprove={(day,rowId,approve)=>setModal({email,day,rowId,type:"line",approve})} />
            )}
          </div>
        );
      })}
    </div>
  );
}

// ─── Line Approval Table ──────────────────────────────────────────────────────
function LineApprovalTable({ email, timesheets, viewYear, viewMonth, days, onApprove }) {
  const [open, setOpen] = useState(true);
  const data=timesheets[email]||{};
  const entries=[];
  for(let d=1;d<=days;d++){
    const dk=dateKey(viewYear,viewMonth,d);
    (data[dk]||[]).forEach(r=>{
      if(r.hours&&parseFloat(r.hours)>0)
        entries.push({day:d,rowId:r.id,project:r.project,hours:parseFloat(r.hours),lineStatus:r.lineStatus||STATUS.SUBMITTED,lineApprovedBy:r.lineApprovedBy,lineRejectedBy:r.lineRejectedBy,lineNote:r.lineNote});
    });
  }
  if(!entries.length)return null;
  const approvedCount=entries.filter(e=>e.lineStatus===STATUS.APPROVED).length;
  const pendingCount=entries.filter(e=>!e.lineStatus||e.lineStatus===STATUS.SUBMITTED||e.lineStatus===STATUS.DRAFT).length;
  const rejectedCount=entries.filter(e=>e.lineStatus===STATUS.REJECTED).length;

  return (
    <div>
      <button onClick={()=>setOpen(o=>!o)}
        style={{ background:"none", border:"none", color:C.textMuted, cursor:"pointer", padding:"9px 18px", fontFamily:"'DM Sans', sans-serif", fontSize:"11px", letterSpacing:"0.08em", fontWeight:"600", display:"flex", alignItems:"center", gap:"8px", width:"100%", transition:"color 0.15s" }}>
        <span style={{ fontSize:"9px" }}>{open?"▲":"▼"}</span>
        LINE ENTRIES ({entries.length})
        {approvedCount>0 && <span style={{ background:C.approvedBg, color:C.approved, border:`1px solid ${C.approvedBorder}`, padding:"1px 8px", borderRadius:"20px", fontSize:"9px", fontWeight:"700" }}>{approvedCount} approved</span>}
        {pendingCount>0  && <span style={{ background:C.submittedBg, color:C.submitted, border:`1px solid ${C.submittedBorder}`, padding:"1px 8px", borderRadius:"20px", fontSize:"9px", fontWeight:"700" }}>{pendingCount} pending</span>}
        {rejectedCount>0 && <span style={{ background:C.rejectedBg, color:C.rejected, border:`1px solid ${C.rejectedBorder}`, padding:"1px 8px", borderRadius:"20px", fontSize:"9px", fontWeight:"700" }}>{rejectedCount} rejected</span>}
      </button>
      {open && (
        <div style={{ padding:"0 18px 14px" }}>
          <div style={{ display:"grid", gridTemplateColumns:"56px 100px 1fr 70px 110px 110px", gap:"8px", marginBottom:"6px", padding:"0 4px" }}>
            {["DAY","DATE","PROJECT","HOURS","APPROVED BY","ACTIONS"].map(h=>(
              <div key={h} style={{ fontSize:"9px", color:C.textMuted, letterSpacing:"0.1em", fontWeight:"700" }}>{h}</div>
            ))}
          </div>
          {entries.map(({day,rowId,project,hours,lineStatus,lineApprovedBy,lineRejectedBy,lineNote})=>{
            const pc=PROJECT_COLORS[project]||{bg:C.surfaceAlt,color:C.textMid,border:C.border};
            const isLineApproved=lineStatus===STATUS.APPROVED;
            const isLineRejected=lineStatus===STATUS.REJECTED;
            return (
              <div key={rowId} style={{ display:"grid", gridTemplateColumns:"56px 100px 1fr 70px 110px 110px", gap:"8px", padding:"7px 4px", borderBottom:`1px solid ${C.borderLight}`, alignItems:"center", background:isLineApproved?"#E8F3F2":isLineRejected?"#E3E4FA":"transparent", borderRadius:"4px" }}>
                <div style={{ fontSize:"11px", color:C.textMid, fontWeight:"500" }}>{getDayName(viewYear,viewMonth,day)}</div>
                <div style={{ fontSize:"11px", color:C.textMuted }}>{MONTH_NAMES[viewMonth].slice(0,3)} {day}</div>
                <div><ProjectBadge project={project} /></div>
                <div style={{ fontSize:"12px", color:C.accent, fontWeight:"700", textAlign:"center", fontFamily:"'DM Mono', monospace" }}>{hours.toFixed(1)}</div>
                <div style={{ fontSize:"10px", overflow:"hidden" }}>
                  {isLineApproved&&lineApprovedBy && <span style={{ color:"#2C497F", fontWeight:"700", display:"block", whiteSpace:"nowrap", overflow:"hidden", textOverflow:"ellipsis" }} title={`Approved by ${lineApprovedBy}`}>✓ {lineApprovedBy}</span>}
                  {isLineRejected&&lineRejectedBy && <span style={{ color:C.rejected, fontWeight:"700", display:"block", whiteSpace:"nowrap", overflow:"hidden", textOverflow:"ellipsis" }} title={`Rejected — ${lineNote||""}`}>🚫 {lineRejectedBy}</span>}
                  {!isLineApproved&&!isLineRejected && <span style={{ color:C.textMuted }}>Pending review</span>}
                </div>
                <div style={{ display:"flex", gap:"5px" }}>
                  <button className={`btn btn-approve${isLineApproved?" active":""}`} onClick={()=>onApprove(day,rowId,true)}>✓</button>
                  <button className={`btn btn-reject${isLineRejected?" active":""}`} onClick={()=>onApprove(day,rowId,false)}>✕</button>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

// ─── Approval Modal ───────────────────────────────────────────────────────────
function ApprovalModal({ action, target, onConfirm, onCancel }) {
  const [note, setNote] = useState("");
  const isReject = action === "Reject";
  return (
    <div className="modal-backdrop">
      <div className="modal-card">
        <div style={{ fontSize:"32px", textAlign:"center", marginBottom:"12px" }}>{isReject?"🚫":"✓"}</div>
        <div style={{ fontSize:"17px", fontWeight:"700", color:C.text, marginBottom:"6px", textAlign:"center" }}>{action} Timesheet</div>
        <div style={{ fontSize:"12px", color:C.textMid, marginBottom:"18px", textAlign:"center" }}>{target}</div>
        <div style={{ marginBottom:"20px" }}>
          <label style={{ fontSize:"10px", color:C.textMuted, fontWeight:"700", letterSpacing:"0.1em", display:"block", marginBottom:"6px" }}>
            {isReject?"REASON FOR REJECTION (required)":"NOTE (optional)"}
          </label>
          <textarea autoFocus value={note} onChange={e=>setNote(e.target.value)}
            placeholder={isReject?"Describe what needs to be corrected…":"Add a note for the member…"}
            style={{ width:"100%", padding:"10px 12px", border:`1.5px solid ${C.border}`, borderRadius:"8px", fontFamily:"'DM Sans', sans-serif", fontSize:"13px", color:C.text, resize:"vertical", minHeight:"80px", outline:"none", transition:"border-color 0.15s", lineHeight:1.5 }}
            onFocus={e=>e.target.style.borderColor=C.accent}
            onBlur={e=>e.target.style.borderColor=C.border}
          />
        </div>
        <div style={{ display:"flex", gap:"10px", justifyContent:"flex-end" }}>
          <button className="btn btn-recall" onClick={onCancel}>Cancel</button>
          <button
            className={`btn ${isReject?"btn-reject active":"btn-approve active"}`}
            style={{ padding:"8px 22px", fontSize:"13px", fontWeight:"700" }}
            onClick={()=>{ if(isReject&&!note.trim()){alert("Please provide a reason for rejection.");return;} onConfirm(note.trim()); }}>
            {action}
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── Root ─────────────────────────────────────────────────────────────────────
function AppWithAuth() {
  const isAuthenticated = useIsAuthenticated();
  return isAuthenticated ? <TimesheetApp /> : <LoginScreen />;
}

export default function App() {
  return (
    <>
      <style>{GLOBAL_STYLES}</style>
      <MsalProvider instance={msalInstance}>
        <AppWithAuth />
      </MsalProvider>
    </>
  );
}
